import { useEffect, useState } from 'react';
import { DEFAULT_SLEEP_CLOTHES, DEFAULT_SLEEP_METHODS, DEFAULT_SLEEP_PLACES, DEFAULT_SLEEP_POSITIONS, can, formatAge, localDate, INVITABLE_ROLES, ROLE_LABEL, type MemberDto, type ReminderSettingsDto, type Role } from '@bb/shared/core';
import { api } from '../api';
import { Chips, Icon, Seg } from '../components';
import { initials, type ThemeMode } from '../lib';
import { platform } from '../platform';
import { useApp } from '../state';

const INTERVALS: { value: string; label: string }[] = [
  { value: 'off', label: 'Выкл' },
  { value: '120', label: '2 ч' },
  { value: '150', label: '2,5 ч' },
  { value: '180', label: '3 ч' },
  { value: '210', label: '3,5 ч' },
  { value: '240', label: '4 ч' },
];

function ChildrenSection() {
  const ctx = useApp();
  const { child, family } = ctx;
  const editable = can(family.role, 'child.edit');
  const isOwner = can(family.role, 'family.manage');
  const today = localDate(Date.now(), family.tz);
  const [name, setName] = useState(child.name);
  const [birthDate, setBirthDate] = useState(child.birthDate);
  const [sex, setSex] = useState<'male' | 'female' | null>(child.sex);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    setName(child.name);
    setBirthDate(child.birthDate);
    setSex(child.sex);
  }, [child]);
  const dirty = name.trim() !== child.name || birthDate !== child.birthDate || sex !== child.sex;

  async function save() {
    setBusy(true);
    try {
      await api.updateChild(child.id, { name: name.trim(), birthDate, sex });
      await ctx.reloadMe();
      ctx.toast({ text: 'Сохранено' });
    } catch (e) {
      ctx.fail(e);
    }
    setBusy(false);
  }

  async function remove() {
    if (!(await platform.confirm(`Удалить ${child.name} и все записи? Это нельзя отменить.`))) return;
    try {
      await api.deleteChild(child.id);
      const other = family.children.find((c) => c.id !== child.id);
      if (other) ctx.selectChild(other.id);
      await ctx.reloadMe();
      ctx.toast({ text: 'Удалено' });
    } catch (e) {
      ctx.fail(e);
    }
  }

  return (
    <section className="card stack" aria-label="Малыши">
      <span className="section-title">Малыши</span>
      {family.children.map((c) => (
        <button key={c.id} type="button" className={`pick${c.id === child.id ? ' on' : ''}`} aria-pressed={c.id === child.id} onClick={() => ctx.selectChild(c.id)} data-testid="child-pick">
          <div className="avatar" style={{ width: 40, height: 40, fontSize: 15 }} aria-hidden="true">
            {initials(c.name)}
          </div>
          <span className="grow" style={{ textAlign: 'left' }}>
            <span style={{ display: 'block', fontWeight: 600 }}>{c.name}</span>
            <span className="tiny muted">{formatAge(c.birthDate, today)}</span>
          </span>
          {c.id === child.id ? <span className="tiny" style={{ fontWeight: 600 }}>выбран</span> : <Icon name="right" size={16} />}
        </button>
      ))}

      <div className="stack" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        <span className="label small" style={{ fontWeight: 600 }}>
          Данные: {child.name}
        </span>
        <div className="field">
          <label htmlFor="s-name">Имя</label>
          <input id="s-name" className="input" value={name} disabled={!editable} maxLength={60} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="s-birth">Дата рождения</label>
          <input id="s-birth" className="input" type="date" value={birthDate} max={today} disabled={!editable} onChange={(e) => setBirthDate(e.target.value)} />
        </div>
        <div className="field">
          <span className="label">Пол — для сравнения роста и веса с нормами ВОЗ</span>
          <Chips<'male' | 'female'> label="Пол" value={sex} options={[{ value: 'female', label: 'Девочка' }, { value: 'male', label: 'Мальчик' }]} onChange={(v) => editable && setSex(v)} />
        </div>
        {editable && dirty && (
          <button type="button" className="btn btn-primary" disabled={busy || !name.trim() || !birthDate} onClick={() => void save()}>
            Сохранить
          </button>
        )}
        {isOwner && family.children.length > 1 && (
          <button type="button" className="btn btn-danger" onClick={() => void remove()}>
            <Icon name="trash" size={18} /> Удалить «{child.name}»
          </button>
        )}
      </div>

      {editable && (adding ? <AddChild onDone={() => setAdding(false)} /> : (
        <button type="button" className="btn btn-soft" onClick={() => setAdding(true)}>
          <Icon name="plus" size={18} /> Добавить малыша
        </button>
      ))}
    </section>
  );
}

function AddChild({ onDone }: { onDone: () => void }) {
  const ctx = useApp();
  const today = localDate(Date.now(), ctx.family.tz);
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [sex, setSex] = useState<'male' | 'female' | null>(null);
  const [busy, setBusy] = useState(false);

  async function add() {
    setBusy(true);
    try {
      const c = await api.addChild(ctx.family.id, { name: name.trim(), birthDate, sex, birthWeightG: null, feeding: null });
      await ctx.reloadMe();
      ctx.selectChild(c.id);
      ctx.toast({ text: `${c.name} добавлен(а)` });
      onDone();
    } catch (e) {
      ctx.fail(e);
    }
    setBusy(false);
  }

  return (
    <div className="stack detail" data-testid="add-child">
      <span className="label small" style={{ fontWeight: 600 }}>
        Новый малыш
      </span>
      <div className="field">
        <label htmlFor="n-name">Имя</label>
        <input id="n-name" className="input" value={name} maxLength={60} placeholder="Например, Миша" onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="n-birth">Дата рождения</label>
        <input id="n-birth" className="input" type="date" value={birthDate} max={today} onChange={(e) => setBirthDate(e.target.value)} />
      </div>
      <Chips<'male' | 'female'> label="Пол" value={sex} options={[{ value: 'female', label: 'Девочка' }, { value: 'male', label: 'Мальчик' }]} onChange={setSex} />
      <div className="tiles2">
        <button type="button" className="btn btn-secondary" onClick={onDone}>
          Отмена
        </button>
        <button type="button" className="btn btn-primary" disabled={busy || !name.trim() || !birthDate} onClick={() => void add()}>
          Добавить
        </button>
      </div>
    </div>
  );
}

type DirKey = 'sleepPlaces' | 'sleepClothes' | 'sleepPositions' | 'sleepMethods';
const DIRS: { key: DirKey; title: string; defaults: string[] }[] = [
  { key: 'sleepPlaces', title: 'Где спит', defaults: DEFAULT_SLEEP_PLACES },
  { key: 'sleepClothes', title: 'Одежда для сна', defaults: DEFAULT_SLEEP_CLOTHES },
  { key: 'sleepPositions', title: 'Как лежит', defaults: DEFAULT_SLEEP_POSITIONS },
  { key: 'sleepMethods', title: 'Как укладываем', defaults: DEFAULT_SLEEP_METHODS },
];

/** Справочники семьи: варианты, которые предлагаются при записи сна. */
function DirectoriesSection() {
  const ctx = useApp();
  const editable = can(ctx.family.role, 'child.edit');
  const s = ctx.family.settings ?? {};
  const [draft, setDraft] = useState<Record<DirKey, string>>({ sleepPlaces: '', sleepClothes: '', sleepPositions: '', sleepMethods: '' });

  async function save(key: DirKey, list: string[]) {
    try {
      await api.saveFamilySettings(ctx.family.id, { [key]: list });
      await ctx.reloadMe();
    } catch (e) {
      ctx.fail(e);
    }
  }

  return (
    <section className="card stack" aria-label="Справочники">
      <span className="section-title">Справочники для записей сна</span>
      {DIRS.map((d) => {
        const list = s[d.key]?.length ? s[d.key]! : d.defaults;
        return (
          <div key={d.key} className="field">
            <span className="label">{d.title}</span>
            <div className="chips">
              {list.map((x) => (
                <span key={x} className="chip dir-chip">
                  {x}
                  {editable && (
                    <button type="button" aria-label={`Убрать «${x}»`} onClick={() => void save(d.key, list.filter((y) => y !== x))}>
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
            {editable && (
              <form
                className="row"
                onSubmit={(e) => {
                  e.preventDefault();
                  const v = draft[d.key].trim();
                  if (!v || list.includes(v)) return;
                  setDraft({ ...draft, [d.key]: '' });
                  void save(d.key, [...list, v]);
                }}
              >
                <input className="input grow" value={draft[d.key]} maxLength={40} placeholder="Добавить вариант" onChange={(e) => setDraft({ ...draft, [d.key]: e.target.value })} aria-label={`${d.title}: новый вариант`} />
                <button type="submit" className="btn btn-soft">
                  <Icon name="plus" size={18} />
                </button>
              </form>
            )}
          </div>
        );
      })}
      {editable && (
        <button type="button" className="link-btn" style={{ alignSelf: 'flex-start' }} onClick={() => void Promise.all(DIRS.map((d) => api.saveFamilySettings(ctx.family.id, { [d.key]: d.defaults }))).then(() => ctx.reloadMe(), ctx.fail)}>
          Вернуть списки по умолчанию
        </button>
      )}
    </section>
  );
}

function FamilySection() {
  const ctx = useApp();
  const { family } = ctx;
  const isOwner = can(family.role, 'family.manage');
  const [members, setMembers] = useState<MemberDto[] | null>(null);
  const [inviteRole, setInviteRole] = useState<Role>('parent');
  const [link, setLink] = useState<string | null>(null);
  const [v, setV] = useState(0);

  useEffect(() => {
    api.members(family.id).then(setMembers, ctx.fail);
  }, [family.id, v, ctx.fail]);

  async function invite() {
    try {
      const res = await api.invite(family.id, inviteRole);
      const url = res.link ?? res.token;
      setLink(url);
      const shared = await platform.shareLink(url, `Присоединяйтесь к дневнику ${ctx.child.name}`);
      if (platform.name === 'browser') ctx.toast({ text: shared ? 'Ссылка скопирована' : 'Скопируйте ссылку ниже' });
    } catch (e) {
      ctx.fail(e);
    }
  }

  async function changeRole(m: MemberDto, role: Role) {
    try {
      await api.updateMember(family.id, m.userId, { role });
      setV((n) => n + 1);
    } catch (e) {
      ctx.fail(e);
    }
  }

  async function remove(m: MemberDto) {
    if (!(await platform.confirm(`Убрать ${m.name} из семьи?`))) return;
    try {
      await api.removeMember(family.id, m.userId);
      setV((n) => n + 1);
    } catch (e) {
      ctx.fail(e);
    }
  }

  return (
    <section className="card stack" aria-label="Семья">
      <span className="section-title">Семья</span>
      {!members ? (
        <div className="skeleton" />
      ) : (
        members.map((m) => (
          <div key={m.userId} className="stack" style={{ gap: 8 }} data-testid="member">
            <div className="row">
              <div className="avatar" style={{ width: 36, height: 36, fontSize: 14 }} aria-hidden="true">
                {initials(m.name)}
              </div>
              <div className="grow">
                <div style={{ fontWeight: 600 }}>
                  {m.name}
                  {m.isMe ? ' (вы)' : ''}
                </div>
                <div className="tiny muted">{ROLE_LABEL[m.role]}</div>
              </div>
              {isOwner && m.role !== 'owner' && (
                <button type="button" className="icon-btn" aria-label={`Убрать ${m.name}`} onClick={() => void remove(m)}>
                  <Icon name="trash" size={18} />
                </button>
              )}
            </div>
            {isOwner && m.role !== 'owner' && (
              <Chips<Role> label={`Роль ${m.name}`} value={m.role} options={INVITABLE_ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] }))} onChange={(r) => void changeRole(m, r)} />
            )}
          </div>
        ))
      )}
      {isOwner && (
        <div className="stack" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <span className="label small" style={{ fontWeight: 600 }}>
            Пригласить
          </span>
          <Seg<Role> label="Роль приглашённого" value={inviteRole} options={INVITABLE_ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] }))} onChange={setInviteRole} />
          <p className="tiny muted">
            {inviteRole === 'relative' ? 'Родственник видит дневник, но не может менять записи.' : inviteRole === 'nanny' ? 'Няня добавляет записи, но не удаляет чужие.' : 'Родитель может всё, кроме управления семьёй.'}
          </p>
          <button type="button" className="btn btn-soft" onClick={() => void invite()}>
            <Icon name="share" size={18} /> Отправить приглашение
          </button>
          {link && (
            <input className="input small" readOnly value={link} aria-label="Ссылка-приглашение" onFocus={(e) => e.target.select()} data-testid="invite-link" />
          )}
          <p className="tiny muted">Ссылка одноразовая и действует 7 дней.</p>
        </div>
      )}
    </section>
  );
}

function RemindersSection() {
  const ctx = useApp();
  const [s, setS] = useState<ReminderSettingsDto | null>(null);

  useEffect(() => {
    api.reminders(ctx.family.id).then(setS, ctx.fail);
  }, [ctx.family.id, ctx.fail]);

  async function save(next: ReminderSettingsDto) {
    setS(next);
    try {
      setS(await api.saveReminders(ctx.family.id, next));
    } catch (e) {
      ctx.fail(e);
    }
  }

  if (!s) return <div className="skeleton" />;
  const quiet = s.quietFrom !== null && s.quietTo !== null;
  return (
    <section className="card stack" aria-label="Напоминания в боте">
      <span className="section-title">Напоминания в боте</span>
      <div className="field">
        <span className="label">Напомнить о кормлении через</span>
        <Chips label="Интервал кормления" value={s.feedIntervalMin ? String(s.feedIntervalMin) : 'off'} options={INTERVALS} onChange={(v) => void save({ ...s, feedIntervalMin: v === 'off' ? null : Number(v) })} />
      </div>
      <div className="row">
        <span className="grow">Итог дня вечером</span>
        <Seg label="Итог дня" value={s.eveningSummary ? 'on' : 'off'} options={[{ value: 'off', label: 'Нет' }, { value: 'on', label: 'Да' }]} onChange={(v) => void save({ ...s, eveningSummary: v === 'on' })} />
      </div>
      {s.eveningSummary && (
        <div className="field">
          <label htmlFor="r-evening">Во сколько</label>
          <input id="r-evening" className="input" type="time" value={s.eveningAt} onChange={(e) => e.target.value && void save({ ...s, eveningAt: e.target.value })} />
        </div>
      )}
      <div className="row">
        <span className="grow">Тихие часы</span>
        <Seg label="Тихие часы" value={quiet ? 'on' : 'off'} options={[{ value: 'off', label: 'Нет' }, { value: 'on', label: 'Да' }]} onChange={(v) => void save({ ...s, quietFrom: v === 'on' ? '23:00' : null, quietTo: v === 'on' ? '07:00' : null })} />
      </div>
      {quiet && (
        <div className="tiles2">
          <div className="field">
            <label htmlFor="r-qf">С</label>
            <input id="r-qf" className="input" type="time" value={s.quietFrom ?? ''} onChange={(e) => e.target.value && void save({ ...s, quietFrom: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="r-qt">До</label>
            <input id="r-qt" className="input" type="time" value={s.quietTo ?? ''} onChange={(e) => e.target.value && void save({ ...s, quietTo: e.target.value })} />
          </div>
        </div>
      )}
      <p className="tiny muted">Чтобы бот мог писать, откройте его чат и нажмите «Старт».</p>
    </section>
  );
}

export function Settings({ onClose }: { onClose: () => void }) {
  const ctx = useApp();
  return (
    <div className="stack">
      <header className="header">
        <button type="button" className="icon-btn" aria-label="Назад" onClick={onClose}>
          <Icon name="back" size={18} />
        </button>
        <h1 className="h2 grow">Настройки</h1>
      </header>
      <ChildrenSection />
      <FamilySection />
      <RemindersSection />
      <DirectoriesSection />
      <section className="card stack" aria-label="Оформление">
        <span className="section-title">Оформление</span>
        <Seg<ThemeMode>
          label="Тема"
          value={ctx.themeMode}
          options={[
            { value: 'auto', label: 'Авто' },
            { value: 'day', label: 'День' },
            { value: 'night', label: 'Ночь' },
          ]}
          onChange={ctx.setThemeMode}
        />
        <p className="tiny muted">«Авто» включает ночную тему с 22:00 до 7:00 и когда в Telegram тёмная тема.</p>
      </section>
    </div>
  );
}
